/**
 * Money and dates, formatted one way for every customer-facing screen.
 *
 * Each public page used to carry its own fmtMoney and fmtDate, and they had
 * drifted: one printed "$52", another "$52.00", a third "$1,069". A customer
 * comparing a quote against the invoice for the same work should never have to
 * wonder whether those are the same number.
 */

/** $1,069.00 — always two decimals, always grouped. */
export function money(n?: number | null): string {
  return `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** $1,069 — for a headline price where cents are noise. */
export function moneyRound(n?: number | null): string {
  return `$${Math.round(Number(n || 0)).toLocaleString('en-US')}`;
}

/**
 * A bare day ("2026-09-23") means that day in Texas, not midnight UTC — which
 * renders as the evening before and has already put the wrong date on a quote.
 */
function parse(d?: string | null): Date | null {
  if (!d) return null;
  const raw = String(d);
  const iso = /^\d{4}-\d{2}-\d{2}$/.test(raw) ? `${raw}T12:00:00`
    : raw.includes(' ') ? `${raw.split(' ')[0]}T12:00:00`
    : raw;
  const dt = new Date(iso);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

/** Wednesday, September 23, 2026 */
export const longDate = (d?: string | null) =>
  parse(d)?.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }) || '';

/** September 23, 2026 */
export const fullDate = (d?: string | null) =>
  parse(d)?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) || '';

/** Tue, Sep 23 */
export const shortDate = (d?: string | null) =>
  parse(d)?.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) || '';

/** Sep 23 */
export const tinyDate = (d?: string | null) =>
  parse(d)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) || '';

/** 9:30 am — lowercase, because a customer reads it as speech, not as a log line. */
export function clock(t?: string | null): string {
  if (!t) return '';
  const [h, m] = String(t).split(':').map(Number);
  if (Number.isNaN(h)) return '';
  return `${h % 12 || 12}:${String(m || 0).padStart(2, '0')} ${h < 12 ? 'am' : 'pm'}`;
}

/**
 * A property's label, or null when it is a placeholder. "Main property" is
 * not a place the crew can drive to, and it used to show up as the stop's
 * title. "Home" is the same (owner, Oct 7 2026: "why is Home on Ryan's
 * schedule … what is that" — it was a customer's yard labelled Home); the
 * street address shows instead. Same pattern as GENERIC_LABELS in server/routes/jobs.js — the server
 * already blanks these for crew; this catches anything that slips past.
 */
const GENERIC_PLACE_LABELS = /^(main property|property|home|house|residence|n\/a|none|-)$/i;
export function placeLabel(label?: string | null): string | null {
  const t = String(label ?? '').trim();
  return t && !GENERIC_PLACE_LABELS.test(t) ? t : null;
}
